import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Loader2, CheckCircle, XCircle, Home } from 'lucide-react';

const PayPalReturnPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Processing your payment...');
  const [courseTitle, setCourseTitle] = useState('');
  const [lessonTitle, setLessonTitle] = useState('');
  const [purchaseType, setPurchaseType] = useState('course'); // 'course' or 'lesson'
  const [redirectPath, setRedirectPath] = useState('/student/dashboard');

  useEffect(() => {
    const capturePayment = async () => {
      const token = searchParams.get('token');
      const type = searchParams.get('type') || 'course'; // 'course' or 'lesson'
      
      setPurchaseType(type);
      
      if (!token) {
        setStatus('error');
        setMessage('Invalid payment session. No token provided.');
        return;
      }

      try {
        // Use the appropriate endpoint based on purchase type
        const endpoint = type === 'lesson' 
          ? `/payments/paypal/lesson/capture-order/${token}`
          : `/payments/paypal/capture-order/${token}`;
        
        const response = await api.post(endpoint);
        
        if (response.data.success || response.data.status === 'success' || response.data.status === 'completed') {
          setStatus('success');
          
          if (type === 'lesson') {
            setLessonTitle(response.data.lesson_title || 'the lesson');
            setMessage('Lesson unlocked successfully!');
            toast.success('Lesson unlocked! You now have access.');
            
            // For lesson purchases, we'll redirect back to the lesson
            // The lesson_id should be in the response or we go to dashboard
            if (response.data.lesson_id) {
              // Get course_id from response or from transaction
              const courseId = response.data.course_id;
              if (courseId) {
                setRedirectPath(`/courses/${courseId}/lessons/${response.data.lesson_id}`);
              }
            }
          } else {
            setCourseTitle(response.data.course_title || 'your course');
            setMessage('Payment successful! You are now enrolled.');
            toast.success('Payment completed successfully!');
          }
          
          // Redirect after 3 seconds
          setTimeout(() => {
            navigate(redirectPath);
          }, 3000);
        } else {
          setStatus('error');
          setMessage(response.data.message || 'Payment could not be completed.');
        }
      } catch (error) {
        console.error('Payment capture error:', error);
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Failed to process payment. Please contact support.');
      }
    };

    capturePayment();
  }, [searchParams, navigate, redirectPath]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#FAF9F6' }}>
      <Card className="max-w-md w-full" style={{ borderColor: '#E3E1DB' }}>
        <CardContent className="p-8 text-center">
          {status === 'processing' && (
            <>
              <Loader2 className="h-16 w-16 mx-auto mb-6 animate-spin" style={{ color: '#163329' }} />
              <h1 
                className="text-2xl font-bold mb-2"
                style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
              >
                Processing Payment
              </h1>
              <p style={{ color: '#5C635A' }}>{message}</p>
              <p className="text-sm mt-4" style={{ color: '#888' }}>
                Please do not close this window...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div 
                className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
                style={{ backgroundColor: '#E8F5E9' }}
              >
                <CheckCircle className="h-10 w-10" style={{ color: '#2E7D32' }} />
              </div>
              <h1 
                className="text-2xl font-bold mb-2"
                style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
              >
                {purchaseType === 'lesson' ? 'Lesson Unlocked!' : 'Payment Successful!'}
              </h1>
              <p className="mb-2" style={{ color: '#5C635A' }}>
                {message}
              </p>
              {purchaseType === 'lesson' && lessonTitle && (
                <p className="text-sm mb-6" style={{ color: '#5C635A' }}>
                  You now have access to <strong>{lessonTitle}</strong>
                </p>
              )}
              {purchaseType === 'course' && courseTitle && (
                <p className="text-sm mb-6" style={{ color: '#5C635A' }}>
                  You now have access to <strong>{courseTitle}</strong>
                </p>
              )}
              <p className="text-sm mb-6" style={{ color: '#888' }}>
                Redirecting...
              </p>
              <Button
                onClick={() => navigate(redirectPath)}
                className="rounded-full"
                style={{ backgroundColor: '#163329' }}
              >
                <Home className="h-4 w-4 mr-2" />
                {purchaseType === 'lesson' ? 'Go to Lesson' : 'Go to Dashboard'}
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div 
                className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
                style={{ backgroundColor: '#FFEBEE' }}
              >
                <XCircle className="h-10 w-10" style={{ color: '#C62828' }} />
              </div>
              <h1 
                className="text-2xl font-bold mb-2"
                style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
              >
                Payment Failed
              </h1>
              <p className="mb-6" style={{ color: '#5C635A' }}>
                {message}
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/courses')}
                  className="w-full rounded-full"
                  style={{ backgroundColor: '#163329' }}
                >
                  Try Again
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="w-full rounded-full"
                  style={{ borderColor: '#163329', color: '#163329' }}
                >
                  Return Home
                </Button>
              </div>
              <p className="text-xs mt-6" style={{ color: '#888' }}>
                If you continue to experience issues, please contact support at{' '}
                <a href="mailto:support@insightevolution.com" className="underline">
                  support@insightevolution.com
                </a>
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PayPalReturnPage;
